import { IClientData } from "../../models/ClientData";
import { RuleProperties } from "json-rules-engine";
import {
  evaluateConditions,
  IConditionEvalResult,
  TRuleFacts,
  TRuleValue,
} from "../../config/jsonRulesEngine";
import {
  IAuthState,
  IJourneyFacts,
  IClientFacts,
  IMilestoneState,
} from "../types/journeyInput.types";
import {
  calculateAge,
  toNumber,
} from "../../helpers/AssetHelpers/AssetHelpers";
import { factClientFirstIntent } from "../utils/factMapping/businessLogicHelpers";

type FactSource = IJourneyFacts;

export function buildMilestonesRecord(
  client: IClientData | null
): Record<string, IMilestoneState> {
  const milestoneMap: Record<string, IMilestoneState> = {};
  const milestones = client?.annuity?.milestones ?? [];

  for (const milestone of milestones) {
    if (!milestone?.milestoneId) continue;

    const existing = milestoneMap[milestone.milestoneId];
    const existingTime = existing?.completedDate
      ? new Date(existing.completedDate).getTime()
      : NaN;
    const candidateTime = milestone.completedDate
      ? new Date(milestone.completedDate).getTime()
      : NaN;

    // The same milestoneId can appear multiple times in the raw array, not
    // necessarily in chronological order — keep whichever occurrence has the
    // latest completedDate rather than whichever comes last in the array.
    if (existing && !Number.isNaN(existingTime) && Number.isNaN(candidateTime)) {
      continue;
    }
    if (existing && candidateTime <= existingTime) {
      continue;
    }

    milestoneMap[milestone.milestoneId] = {
      completed: milestone.completed === true,
      completedDate: milestone.completedDate,
    };
  }

  return milestoneMap;
}

export function mapClientDataToFacts(client: IClientData | null): IClientFacts {
  const milestoneMap = buildMilestonesRecord(client);
  const clientSource = client?.regSrc ?? "";
  const clientFirstIntent = factClientFirstIntent(client);
  const pensions = client?.pensions ?? [];

  return {
    clientSource,
    age: calculateAge(client?.dateOfBirth) ?? 0,
    fundAmount: toNumber(client?.annuity?.pensionFund) ?? 0,
    totalPensionValue: pensions.reduce(
      (sum, pension) => sum + (pension.valuation ?? 0),
      0
    ),
    isRetired: client?.employmentStatus === "Retired",
    clientFirstIntent,
    hasPensions: pensions.length > 0,
    milestones: milestoneMap,
  };
}

export function buildJourneyFacts(
  clientData: IClientData | null,
  auth: IAuthState
): IJourneyFacts {
  return {
    ...auth,
    ...mapClientDataToFacts(clientData),
  };
}

export function toRuleFacts(facts: FactSource): TRuleFacts {
  const ruleFacts: TRuleFacts = {};

  Object.keys(facts).forEach((key) => {
    ruleFacts[key] = facts[key as keyof typeof facts] as TRuleValue;
  });

  return ruleFacts;
}

export function evaluateRuleConditions(
  conditions: RuleProperties["conditions"],
  facts: TRuleFacts
): Promise<IConditionEvalResult> {
  return evaluateConditions(conditions, facts);
}
